import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ShieldCheck, ShieldX, Calendar, MapPin, User, Building, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyInvitation = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [number, setNumber] = useState(searchParams.get('number') || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);

  const t = language === 'en' ? {
    title: "Verify Invitation",
    subtitle: "Enter the visa invitation number to verify its authenticity.",
    placeholder: "e.g. TK-202604-A1B2C3D4",
    verify: "Verify",
    verifying: "Verifying...",
    valid: "VALID INVITATION",
    validMsg: "This is an authentic invitation issued by TALITHA KOUMI TU ES UNE GUERRIÈRE.",
    invalid: "INVITATION NOT VALID",
    invalidMsg: "This invitation number was not found in our system. Please verify the number and try again.",
    participant: "Participant",
    nationality: "Nationality",
    passport: "Passport (partial)",
    conference: "Conference",
    dates: "Dates",
    venue: "Venue",
    status: "Status",
    issuedBy: "Issued By",
    regNumber: "Registration No.",
    back: "Back to Home",
    statusDraft: "Draft — Pending Approval",
    statusApproved: "Approved & Sent",
  } : {
    title: "Vérifier une Invitation",
    subtitle: "Entrez le numéro d'invitation visa pour vérifier son authenticité.",
    placeholder: "ex. TK-202604-A1B2C3D4",
    verify: "Vérifier",
    verifying: "Vérification...",
    valid: "INVITATION VALIDE",
    validMsg: "Ceci est une invitation authentique émise par TALITHA KOUMI TU ES UNE GUERRIÈRE.",
    invalid: "INVITATION NON VALIDE",
    invalidMsg: "Ce numéro d'invitation n'a pas été trouvé dans notre système. Veuillez vérifier le numéro et réessayer.",
    participant: "Participant(e)",
    nationality: "Nationalité",
    passport: "Passeport (partiel)",
    conference: "Conférence",
    dates: "Dates",
    venue: "Lieu",
    status: "Statut",
    issuedBy: "Émis par",
    regNumber: "N° d'enregistrement",
    back: "Retour à l'Accueil",
    statusDraft: "Brouillon — En attente d'approbation",
    statusApproved: "Approuvée et Envoyée",
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!number.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/verify-invitation/${number.trim()}`);
      setResult(resp.data);
    } catch (err) {
      setResult({ valid: false });
    }
    setSearched(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <div className="bg-[#1D1D1D] py-3 px-4">
        <div className="max-w-3xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg" alt="Logo" className="h-10" />
            <span className="text-white/50 text-xs font-bold tracking-widest hidden sm:inline">VERIFICATION PORTAL</span>
          </Link>
          <Link to="/" className="text-white/60 hover:text-white flex items-center gap-1 text-sm">
            <ArrowLeft className="w-4 h-4" /> {t.back}
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-extrabold text-[#1D1D1D] mb-2">{t.title}</h1>
          <p className="text-gray-500 text-sm">{t.subtitle}</p>
        </div>

        <form onSubmit={handleVerify} data-testid="verify-invitation-form" className="bg-white rounded-2xl border border-gray-100 p-6 mb-8">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                data-testid="verify-invitation-input"
                type="text"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder={t.placeholder}
                className="w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl text-sm font-mono focus:ring-2 focus:ring-[#CD0000] focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="verify-invitation-submit"
              className="bg-[#1D1D1D] text-white px-8 py-3.5 rounded-xl font-bold text-sm hover:bg-[#333] transition-colors disabled:opacity-50 shrink-0"
            >
              {loading ? t.verifying : t.verify}
            </button>
          </div>
        </form>

        {searched && result && (
          result.valid ? (
            <div data-testid="verify-result-valid" className="bg-white rounded-2xl border-2 border-[#D4AF37] overflow-hidden">
              {/* Valid Header */}
              <div className="bg-[#D4AF37]/10 p-6 flex items-center gap-4 border-b border-[#D4AF37]/20">
                <div className="w-14 h-14 bg-[#D4AF37] rounded-full flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-extrabold text-[#1D1D1D]">{t.valid}</h2>
                  <p className="text-gray-600 text-xs mt-0.5">{t.validMsg}</p>
                </div>
              </div>

              {/* Badge Content */}
              <div className="p-6">
                <div className="bg-[#1D1D1D] rounded-2xl p-6 text-white mb-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-white/50 text-xs font-bold uppercase tracking-widest">Invitation No.</p>
                      <p className="text-xl font-extrabold font-mono tracking-wider text-[#CD0000]">{result.invitation_number}</p>
                    </div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                      result.status === 'approved' ? 'bg-[#D4AF37] text-white' : 'bg-yellow-500 text-white'
                    }`}>
                      {result.status === 'approved' ? t.statusApproved : t.statusDraft}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-white/40 text-xs mb-1">{t.participant}</p>
                      <p className="font-bold text-sm flex items-center gap-1"><User className="w-3.5 h-3.5 text-white/50" /> {result.participant?.name}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">{t.nationality}</p>
                      <p className="font-bold text-sm">{result.participant?.nationality}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">{t.passport}</p>
                      <p className="font-bold text-sm font-mono">{result.participant?.passport_number}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">{t.dates}</p>
                      <p className="font-bold text-sm flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-white/50" /> {result.dates}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">{t.conference}</p>
                    <p className="font-bold text-[#1D1D1D]">{result.conference}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">{t.venue}</p>
                    <p className="font-medium flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {result.venue}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">{t.issuedBy}</p>
                    <p className="font-medium flex items-center gap-1"><Building className="w-3.5 h-3.5 text-gray-400" /> {result.organization}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">{t.regNumber}</p>
                    <p className="font-mono text-xs text-gray-600">{result.registration_number}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div data-testid="verify-result-invalid" className="bg-white rounded-2xl border-2 border-red-300 p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldX className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-lg font-extrabold text-red-800 mb-2">{t.invalid}</h2>
              <p className="text-gray-500 text-sm">{t.invalidMsg}</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default VerifyInvitation;
